import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import Table, {
  dateFormatter,
  buttonsStyle,
} from "../../../components/tables/table";
import {
  getMigrationUsers,
  getMigrationStats,
  sendMigrationReminder,
  getPaymentHistory,
  updateMigrationNextPayment,
} from "../../../../api/payment";
import { Button, TextField, Chip } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { Autorenew as FilterOffIcon } from "@material-ui/icons";
import EmailIcon from "@material-ui/icons/Email";
import HistoryIcon from "@material-ui/icons/History";
import EditIcon from "@material-ui/icons/Edit";
import { alertError, alertSuccess } from "../../../../utils/logger";
import Select from "react-select";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import GetAppIcon from "@material-ui/icons/GetApp";
import { shallowEqual, useSelector } from "react-redux";
import { canEditPaymentMigration } from "../../../../_helpers/backofficePermissions";
import {
  exportMigracionesExcel,
  filterMigrationRowsForExport,
} from "../../../../utils/exportMigracionesExcel";

function getData(users) {
  return users.map((user) => ({
    id: user._id,
    dni: user.dni || "—",
    name: (user.fullName + " " + user.apellido1 + " " + user.apellido2).trim(),
    email: user.email,
    nAso: user.nAso,
    tipoUsuario: user.type?.titulo || "N/A",
    metodoPago: user.metodoPago || "iban",
    estadoMigracion: user.estadoMigracion || "pendiente",
    tipoCuota: user.tipoCuota || null,
    fechaEfectuada:
      user.estadoMigracion === "completada" ? user.fechaPagoMigracion : null,
    ultimoPago: user.ultimoPago,
    proximoPago: user.proximoPago,
    fechaMigracion: user.fechaMigracionTarjeta,
    intentosFallidos: user.intentosPagoFallidos || 0,
  }));
}

const initialFilters = {
  estado: null,
  metodoPago: null,
  search: "",
};

export default function PaymentManagementPage() {
  const authUser = useSelector(
    (store) => store.authentication?.user,
    shallowEqual,
  );
  const readOnlyMigration = !canEditPaymentMigration(authUser?.role);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState(() => {
    const saved = localStorage.getItem("paymentPageFilters");
    return saved ? JSON.parse(saved) : initialFilters;
  });
  const [searchText, setSearchText] = useState(() => {
    const saved = localStorage.getItem("paymentPage_searchText");
    return saved || "";
  });
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(0);
  const [selectedUserIdForReminder, setSelectedUserIdForReminder] = useState(
    null,
  );
  const [sendingReminders, setSendingReminders] = useState(false);
  const [openNextPaymentDialog, setOpenNextPaymentDialog] = useState(false);
  const [selectedRowForNextPayment, setSelectedRowForNextPayment] =
    useState(null);
  const [nextPaymentInputValue, setNextPaymentInputValue] = useState("");
  const [savingNextPayment, setSavingNextPayment] = useState(false);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, []);

  useEffect(() => {
    if (data.length) {
      handleSearch();
    }
  }, [filterOptions]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterOptions.estado) params.estado = filterOptions.estado;

      const response = await getMigrationUsers(params);
      const formattedData = getData(response.data);
      setData(formattedData);
      setFilteredData(formattedData);
    } catch (error) {
      alertError({
        error,
        customMessage: "Error al obtener usuarios.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getMigrationStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error al obtener estadísticas:", error);
    }
  };

  const handleTableSearchChange = React.useCallback((value) => {
    const trimmedValue = value ? value : "";
    setSearchText(trimmedValue);
    if (trimmedValue) {
      localStorage.setItem("paymentPage_searchText", trimmedValue);
    } else {
      localStorage.removeItem("paymentPage_searchText");
    }
  }, []);

  const handleSearch = () => {
    localStorage.setItem("paymentPageFilters", JSON.stringify(filterOptions));
    fetchData();
  };

  const handleClearFilters = () => {
    setFilterOptions(initialFilters);
    setSearchText("");
    localStorage.removeItem("paymentPageFilters");
    localStorage.removeItem("paymentPage_searchText");
    localStorage.removeItem("paymentPage_currentPage");
    fetchData();
  };

  const estadoFiltroLabel = () => {
    if (!filterOptions.estado) return "Todos los estados";
    const map = {
      pendiente: "Pendiente",
      completada: "Completada",
      rechazada: "Rechazada",
    };
    return map[filterOptions.estado] || filterOptions.estado;
  };

  const handleExportExcel = () => {
    const rows = filterMigrationRowsForExport(filteredData, searchText);
    if (!rows.length) {
      alertError({
        customMessage:
          "No hay filas que exportar con los filtros y la búsqueda actuales.",
      });
      return;
    }
    const busqueda = searchText.trim()
      ? `Búsqueda: «${searchText.trim()}»`
      : "Sin texto de búsqueda en tabla";
    const titleLine = `Migración de pagos — ${estadoFiltroLabel()} — ${busqueda} (${rows.length} fila(s))`;
    const ok = exportMigracionesExcel({ titleLine, rows });
    if (ok) {
      alertSuccess({
        customMessage: `Se descargó el Excel con ${rows.length} fila(s).`,
      });
    }
  };

  const handleSendReminder = async (userId) => {
    try {
      await sendMigrationReminder(userId);
      alertSuccess({ customMessage: "Email de recordatorio enviado." });
      fetchStats();
      fetchData(); // Refrescar datos para actualizar la lista
    } catch (error) {
      alertError({
        error,
        customMessage: "Error al enviar el email.",
      });
    }
  };

  const handleSendBulkReminders = async () => {
    const pendingUsers = filteredData.filter(
      (user) => user.estadoMigracion === "pendiente",
    );

    if (pendingUsers.length === 0) {
      alertError({
        customMessage:
          "No hay usuarios con estado pendiente para enviar recordatorios.",
      });
      setOpenConfirmDialog(0);
      return;
    }

    setSendingReminders(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Enviar recordatorios uno por uno
      for (const user of pendingUsers) {
        try {
          await sendMigrationReminder(user.id);
          successCount++;
        } catch (error) {
          console.error(`Error enviando recordatorio a ${user.email}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        alertSuccess({
          customMessage: `Se enviaron ${successCount} recordatorio(s) correctamente.${
            errorCount > 0 ? ` ${errorCount} fallaron.` : ""
          }`,
        });
      }

      if (errorCount > 0 && successCount === 0) {
        alertError({ customMessage: "Error al enviar los recordatorios." });
      }

      fetchStats();
      fetchData(); // Refrescar datos
    } catch (error) {
      alertError({
        error,
        customMessage: "Error al enviar los recordatorios.",
      });
    } finally {
      setSendingReminders(false);
      setOpenConfirmDialog(0);
    }
  };

  const handleViewHistory = async (userId) => {
    setSelectedUser(userId);
    setOpenHistoryDialog(true);
    setLoadingHistory(true);
    try {
      const response = await getPaymentHistory(userId);
      setPaymentHistory(response.data || []);
    } catch (error) {
      alertError({
        error,
        customMessage: "Error al obtener el historial.",
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDateForDateInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    // Ajuste a fecha local para que el input type="date" muestre el día esperado
    const tzOffsetMinutes = d.getTimezoneOffset();
    const local = new Date(d.getTime() - tzOffsetMinutes * 60_000);
    return local.toISOString().slice(0, 10);
  };

  const handleOpenNextPaymentDialog = (row) => {
    if (readOnlyMigration) return;
    setSelectedRowForNextPayment(row);
    setNextPaymentInputValue(formatDateForDateInput(row?.proximoPago));
    setOpenNextPaymentDialog(true);
  };

  const handleSaveNextPayment = async () => {
    if (readOnlyMigration) return;
    if (!selectedRowForNextPayment?.id) return;
    setSavingNextPayment(true);
    try {
      const proximoPago = nextPaymentInputValue
        ? nextPaymentInputValue
        : null;
      await updateMigrationNextPayment(selectedRowForNextPayment.id, proximoPago);
      alertSuccess({ customMessage: "Próximo pago actualizado." });
      setOpenNextPaymentDialog(false);
      setSelectedRowForNextPayment(null);
      setNextPaymentInputValue("");
      fetchData();
    } catch (error) {
      alertError({
        error,
        customMessage: "Error al actualizar el próximo pago.",
      });
    } finally {
      setSavingNextPayment(false);
    }
  };

  const renderFiltersContent = () => {
    return (
      <div style={{ padding: "5px 0 0 0" }}>
        <div className="row align-items-end">
          <div className="col-md-3 col-sm-6 mb-3">
            <TextField
              id="search"
              label="Buscar"
              placeholder="DNI, nombre, email, número de socio..."
              value={searchText}
              onChange={(e) => handleTableSearchChange(e.target.value)}
              margin="dense"
              variant="outlined"
              fullWidth
              style={{ marginTop: 0, marginBottom: 0 }}
            />
          </div>

          <div className="col-md-3 col-sm-6 mb-3">
            <Select
              placeholder="Estado de migración"
              value={
                filterOptions.estado
                  ? { value: filterOptions.estado, label: filterOptions.estado }
                  : null
              }
              onChange={(option) =>
                setFilterOptions({
                  ...filterOptions,
                  estado: option ? option.value : null,
                })
              }
              options={[
                { value: "pendiente", label: "Pendiente" },
                { value: "completada", label: "Completada" },
                { value: "rechazada", label: "Rechazada" },
              ]}
              isClearable
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "40px",
                }),
              }}
            />
          </div>
        </div>
        <div className="row align-items-center mt-3">
          <div
            className="col-md-12 d-flex justify-content-end flex-wrap"
            style={{ gap: "10px" }}
          >
            <Button
              variant="contained"
              onClick={handleExportExcel}
              startIcon={<GetAppIcon />}
              style={{
                backgroundColor: "#2e7d32",
                color: "white",
              }}
            >
              Exportar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              style={{
                backgroundColor: "#f3691a",
                color: "white",
              }}
            >
              Buscar
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<FilterOffIcon />}
              style={{
                borderColor: "#f3691a",
                color: "#f3691a",
              }}
            >
              Limpiar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const estadoFormatter = (cell, row) => {
    const colors = {
      pendiente: "default",
      completada: "primary",
      rechazada: "secondary",
    };
    return <Chip label={cell} color={colors[cell] || "default"} size="small" />;
  };

  const metodoPagoFormatter = (cell) => {
    return cell === "tarjeta" ? (
      <Chip label="Tarjeta" color="primary" size="small" />
    ) : (
      <Chip label="IBAN" color="default" size="small" />
    );
  };

  const buttonFormatter = (cell, row) => {
    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!readOnlyMigration && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => handleOpenNextPaymentDialog(row)}
            style={{ minWidth: "auto", padding: "8px" }}
            title="Editar próximo pago"
          >
            <EditIcon />
          </Button>
        )}
        {!readOnlyMigration && row.estadoMigracion === "pendiente" && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => {
              setSelectedUserIdForReminder(row.id);
              setOpenConfirmDialog(2);
            }}
            style={{ minWidth: "auto", padding: "8px" }}
            title="Enviar recordatorio"
          >
            <EmailIcon />
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          color="default"
          onClick={() => handleViewHistory(row.id)}
          style={{ minWidth: "auto", padding: "8px" }}
          title="Ver historial"
        >
          <HistoryIcon />
        </Button>
      </div>
    );
  };

  const columns = [
    {
      dataField: "dni",
      text: "DNI",
      sort: true,
    },
    {
      dataField: "name",
      text: "Nombre",
      sort: true,
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
    },
    {
      dataField: "nAso",
      text: "Nº Socio",
      sort: true,
    },
    {
      dataField: "tipoUsuario",
      text: "Tipo",
      sort: true,
    },
    {
      dataField: "metodoPago",
      text: "Método",
      formatter: metodoPagoFormatter,
      sort: true,
    },
    {
      dataField: "estadoMigracion",
      text: "Estado",
      formatter: estadoFormatter,
      sort: true,
    },
    {
      dataField: "tipoCuota",
      text: "Cuota",
      formatter: (cell) => {
        if (!cell) return "—";
        return cell === "anual" ? "Anual" : "Semestral";
      },
      sort: true,
    },
    {
      dataField: "fechaEfectuada",
      text: "Fecha efectuada",
      formatter: dateFormatter,
      sort: true,
    },
    {
      dataField: "ultimoPago",
      text: "Último Pago",
      formatter: dateFormatter,
      sort: true,
    },
    {
      dataField: "proximoPago",
      text: "Próximo Pago",
      formatter: dateFormatter,
      sort: true,
    },
    {
      dataField: "intentosFallidos",
      text: "Intentos Fallidos",
      sort: true,
    },
    {
      dataField: "actions",
      text: "Acciones",
      formatter: buttonFormatter,
      sort: false,
      headerStyle: { width: "10%" },
    },
  ];

  const pendingCount = filteredData.filter(
    (user) => user.estadoMigracion === "pendiente",
  ).length;

  return (
    <>
      <Card>
        <CardHeader title="Gestión de Migraciones">
          <CardHeaderToolbar>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenConfirmDialog(1)}
              startIcon={<RecordVoiceOverIcon />}
              disabled={
                readOnlyMigration || pendingCount === 0 || sendingReminders
              }
              style={{ backgroundColor: "#f3691a", color: "white" }}
            >
              RECORDATORIO
            </Button>
          </CardHeaderToolbar>
          {stats && (
            <div
              className="d-flex mt-3"
              style={{ gap: "20px", flexWrap: "wrap", alignItems: "center" }}
            >
              <div>
                <strong>Total Socios:</strong> {stats.totalSocios}
              </div>
              <div>
                <strong>Migrados:</strong>{" "}
                <Chip label={stats.migrados} color="primary" size="small" />
              </div>
              <div>
                <strong>Pendientes:</strong>{" "}
                <Chip label={stats.pendientes} color="default" size="small" />
              </div>
              <div>
                <strong>% Migrado:</strong> {stats.porcentajeMigrado}%
              </div>
            </div>
          )}
        </CardHeader>
        <CardBody style={{ paddingTop: "15px" }}>
          {/* Filtros siempre visibles con mejor diseño */}
          <Card style={{ marginBottom: "15px", backgroundColor: "#f8f9fa" }}>
            <CardBody style={{ padding: "15px 20px" }}>
              {renderFiltersContent()}
            </CardBody>
          </Card>
          <Table
            columns={columns}
            data={filteredData}
            pageKey="paymentPage"
            searchText={searchText}
            onSearchChange={handleTableSearchChange}
            loading={loading}
          />
        </CardBody>
      </Card>

      {/* Dialog de Historial */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Historial de Pagos</DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Typography>Cargando...</Typography>
          ) : paymentHistory.length === 0 ? (
            <Typography>No hay pagos registrados.</Typography>
          ) : (
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Importe</TableCell>
                  <TableCell>Método</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Tipo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((payment) => (
                  <TableRow key={payment._id}>
                    <TableCell>
                      {payment.fechaPago
                        ? new Date(payment.fechaPago).toLocaleDateString(
                            "es-ES",
                          )
                        : "-"}
                    </TableCell>
                    <TableCell>{payment.amount}€</TableCell>
                    <TableCell>
                      {payment.metodoPago === "tarjeta"
                        ? "Tarjeta"
                        : payment.metodoPago === "paypal"
                        ? "PayPal"
                        : payment.metodoPago === "gratis"
                        ? "Gratis"
                        : "IBAN"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.estado}
                        color={
                          payment.estado === "completado"
                            ? "primary"
                            : payment.estado === "fallido"
                            ? "secondary"
                            : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{payment.tipo}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ConfirmDialog para recordatorio masivo */}
      <ConfirmDialog
        open={openConfirmDialog === 1}
        setOpen={(value) => {
          if (!value) {
            setOpenConfirmDialog(0);
          }
        }}
        onConfirm={handleSendBulkReminders}
        title="¿Enviar recordatorios a todos los pendientes?"
      >
        <p>
          Se enviará un email de recordatorio a {pendingCount} usuario(s) con
          estado pendiente para que pasen a pago por tarjeta. ¿Deseas continuar?
        </p>
      </ConfirmDialog>

      {/* ConfirmDialog para recordatorio individual */}
      <ConfirmDialog
        open={openConfirmDialog === 2}
        setOpen={(value) => {
          if (!value) {
            setOpenConfirmDialog(0);
            setSelectedUserIdForReminder(null);
          }
        }}
        onConfirm={async () => {
          if (selectedUserIdForReminder) {
            await handleSendReminder(selectedUserIdForReminder);
            setOpenConfirmDialog(0);
            setSelectedUserIdForReminder(null);
          }
        }}
        title="¿Enviar recordatorio?"
      >
        <p>
          Se enviará un email de recordatorio a este usuario para que pase a
          pago por tarjeta. ¿Deseas continuar?
        </p>
      </ConfirmDialog>

      {/* Dialog para editar próximo pago */}
      <Dialog
        open={openNextPaymentDialog}
        onClose={() => {
          if (savingNextPayment) return;
          setOpenNextPaymentDialog(false);
          setSelectedRowForNextPayment(null);
          setNextPaymentInputValue("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar Próximo Pago</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: "12px" }}>
            {selectedRowForNextPayment?.name
              ? selectedRowForNextPayment.name
              : "Usuario"}
          </Typography>
          <TextField
            label="Próximo pago"
            type="date"
            value={nextPaymentInputValue}
            onChange={(e) => setNextPaymentInputValue(e.target.value)}
            margin="dense"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={savingNextPayment}
            helperText="Deja vacío para borrar el valor."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenNextPaymentDialog(false);
              setSelectedRowForNextPayment(null);
              setNextPaymentInputValue("");
            }}
            color="default"
            disabled={savingNextPayment}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveNextPayment}
            color="primary"
            variant="contained"
            disabled={savingNextPayment || readOnlyMigration}
            style={{ backgroundColor: "#f3691a", color: "white" }}
          >
            {savingNextPayment ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
